import { useNavigate } from "@solidjs/router"
import { createQuery } from "@urql/solid"
import { values } from "remeda"
import { createMemo, Show } from "solid-js"
import { ulid } from "ulid"
import { urqlClient } from "@/context/graphql-provider"
import { useApplication } from "@/context/session-provider"
import { Button, LinkButton } from "@/designSystem/components/button"
import { Icon } from "@/designSystem/components/icon"
import { Masonry } from "@/designSystem/components/masonry"
import {
  PageInfoFragment,
  Pagination,
} from "@/designSystem/components/pagination"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { BlankSlate } from "@/designSystem/patterns/blank-slate"
import { Info } from "@/designSystem/patterns/info"
import { NavigationWithButtons } from "@/designSystem/patterns/navigation"
import { PostComponent, PostFragment } from "@/designSystem/patterns/post"
import { PublicationTabs } from "@/designSystem/patterns/publication-tabs"
import SearchForm from "@/designSystem/patterns/search-form"
import { graphql, readFragment } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import {
  createBlogRouter,
  FILTER,
  type Filter,
  LIST_DEFAULTS,
  type ListParams,
} from "@/routes/posts-router"
import {
  mainClass,
  mobileIconClass,
  publicationLinkClass,
  publicationLinkDesktopClass,
  pulseDotClass,
} from "./post-list.css"

const CreatePostMutation = graphql(
  `mutation CreatePostFromList($id: ID!) {
    createPost(id: $id) {
      ...on MutationCreatePostSuccess {
        data {
          id
        }
      }
      ...ErrorFragment
    }
  }`,
  [ErrorFragment],
)

const SearchPostsQuery = graphql(
  `query SearchPosts(
    $status: [PostStatus!]
    $tag: [String!]
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: PostOrderInput
  ) {
    posts(
      status: $status
      tag: $tag
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
    ) {
      edges {
        cursor
        node {
          id
          ...Post
        }
      }
      pageInfo { ...PageInfoFragment }
    }
  }`,
  [PostFragment, PageInfoFragment],
)

function getStatus(params: ListParams) {
  const filter = params.filter
  if (!filter) return []
  if (filter === "published") return ["published" as const, "sent" as const]
  return [filter]
}

function getTag(params: ListParams) {
  if (!params.tag || params.tag === "all") return []
  return [params.tag]
}

function getVariables(params: ListParams) {
  return {
    status: getStatus(params),
    tag: getTag(params),
    order: { field: "createdAt" as const, direction: "desc" as const },
    ...(params.before
      ? { before: params.before, last: 20 }
      : { after: params.after || undefined, first: 20 }),
  }
}

export function preloadPostList() {
  return urqlClient
    .query(SearchPostsQuery, getVariables(LIST_DEFAULTS))
    .toPromise()
}

export function PostList() {
  const { user } = useApplication()
  const { LL } = useI18nContext()
  const navigate = useNavigate()
  const { paths, listParams } = createBlogRouter()
  const variables = createMemo(() => getVariables(listParams()))

  const [postResults] = createQuery({
    query: SearchPostsQuery,
    variables,
    requestPolicy: "cache-and-network",
  })

  const [_createPostState, createPost] = createMutationWithErrorHandling(
    CreatePostMutation,
    "createPost",
  )

  async function onCreatePost() {
    const id = ulid()
    await createPost({ id })

    navigate(paths({ filter: undefined }).post({ id, title: "untitled" }).url)
  }
  const postsMetadata = createMemo(() => postResults.data?.posts)
  const posts = createMemo(() => postsMetadata()?.edges ?? [])
  const pageInfo = createMemo(() => postsMetadata()?.pageInfo)

  const filterValues = createMemo(() => {
    const filterTranslations = {
      all: LL().common.all(),
      spam: LL().blog.filter.spam(),
      archived: LL().blog.filter.archived(),
      sent: LL().blog.filter.sent(),
      published: LL().blog.filter.published(),
      draft: LL().blog.filter.draft(),
    }

    return {
      values: [
        {
          value: "all",
          iconName: "filter" as const,
          hue: "sand" as const,
          label: LL().common.all(),
        },
        ...values(FILTER).map(f => ({
          value: f.value,
          iconName: f.iconName,
          hue: f.hue,
          label: filterTranslations[f.value],
        })),
      ],
      value: listParams().filter ?? "all",
    }
  })

  const tagValues = createMemo(() => {
    const values = Array.from(
      new Set(
        posts().flatMap(
          n => readFragment(PostFragment, n.node).post.tags?.tags ?? [],
        ),
      ),
    ).map(tag => ({ value: tag }))

    return {
      values: [
        {
          value: "all",
          label: LL().common.all(),
        },
        ...values,
      ],
      value: listParams().tag ?? "all",
    }
  })

  const isDefaultPostListView = createMemo(() => {
    const p = listParams()
    if (p.query.trim()) return false
    if (p.tag && p.tag !== "all") return false
    if (p.filter) return false

    return true
  })

  return (
    <MainLayout
      hue="amber"
      header={
        <NavigationWithButtons hue="amber" title={LL().blog.title()}>
          <Show when={user.publication?.url}>
            {url => (
              <LinkButton
                hue="amber"
                kind="surface"
                class={publicationLinkClass}
                href={url()}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="eye" class={mobileIconClass} size="18" />
                <span class={publicationLinkDesktopClass}>
                  <span class={pulseDotClass} />
                  {url().replace(/^https?:\/\//, "")}
                </span>
              </LinkButton>
            )}
          </Show>
          <Button
            hue="amber"
            onClick={onCreatePost}
            active={_createPostState.fetching}
          >
            {LL().common.addPosts()}
          </Button>
        </NavigationWithButtons>
      }
    >
      <div class={mainClass}>
        <PublicationTabs />
        <SearchForm
          onChange={({ query, filter, tag }) => {
            navigate(
              paths({
                filter: filter as Filter,
                tag,
                query,
                after: "",
                before: "",
              }).url,
            )
          }}
          tag={tagValues()}
          filter={filterValues()}
        />
        <Show
          when={!postResults.fetching}
          fallback={<Info>{LL().common.loading()}</Info>}
        >
          <Show
            when={posts()?.length}
            fallback={
              <Show
                when={isDefaultPostListView()}
                fallback={<Info>{LL().blog.noResultsFound()}</Info>}
              >
                <BlankSlate
                  iconName="book-open-text"
                  action={
                    <Button
                      hue="amber"
                      onClick={onCreatePost}
                      active={_createPostState.fetching}
                    >
                      {LL().common.addPosts()}
                    </Button>
                  }
                >
                  {LL().blog.noPostsYet()}
                </BlankSlate>
              </Show>
            }
          >
            <Masonry
              items={posts}
              renderItem={edge => <PostComponent postSearch={edge.node} />}
            />
            <Pagination
              pageInfo={pageInfo()}
              buildUrl={params => paths(params).url}
            />
          </Show>
        </Show>
      </div>
    </MainLayout>
  )
}
